import {
  getFormattedDateTime,
  getFormattedDateTimeRange,
} from '@lmt/unicorn-shared/app/components/patients/common';

import ArrowIcon from '@lmt/unicorn-shared/app/icons/arrow-down.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React from 'react';

import styles from './ConditionParser.module.scss';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useTranslation } from 'react-i18next';

interface Props {
  condition: string | null | undefined;
}

export const ConditionParser: React.FC<Props> = (props) => {
  const { condition } = props;
  const i18next = useScopedTranslation('calendar.color_settings.parser');
  const i18nextGlobal = useTranslation();
  const visitTypes = useVisitTypes();

  if (!condition) {
    return (
      <div className={styles.operationValue}>{i18next.t('no_filter')}</div>
    );
  }

  function convertValue(key: string, value: string | null): string {
    if (value == null) {
      return 'null';
    }
    switch (key) {
      case 'typeId':
        const visitType = visitTypes.find((x) => x.id === parseInt(value));
        return visitType ? i18nextGlobal.t(visitType.value as any) : 'unknown';
      case 'statusId':
        return i18nextGlobal.t(`visit_status.${value}` as any);
      case 'start':
        const startParts = value.split(',');
        if (startParts.length === 1) {
          return getFormattedDateTime(new Date(value));
        } else {
          return getFormattedDateTimeRange(
            new Date(startParts[0].trim()),
            new Date(startParts[1].trim()),
          );
        }
      case 'end':
        const parts = value.split(',');
        if (parts.length === 1) {
          return getFormattedDateTime(new Date(value));
        } else {
          return getFormattedDateTimeRange(
            new Date(parts[0].trim()),
            new Date(parts[1].trim()),
          );
        }

      default:
        return value.toString();
    }
  }

  function convert(condition: string, operation?: string): JSX.Element {
    const filterArray = JSON.parse(condition);
    if (!(filterArray instanceof Array)) {
      throw new Error(`Invalid part of filter: ${JSON.stringify(filterArray)}`);
    }
    if (filterArray.length < 3) {
      throw new Error(`Invalid part of filter: ${JSON.stringify(filterArray)}`);
    }
    if (filterArray.length === 3 && !Array.isArray(filterArray[0])) {
      if (Array.isArray(filterArray[2])) {
        filterArray[2] = `${filterArray[2].join(', ')}`;
      }
      const filterOperation = operation ?? 'and';
      return (
        <div className={styles.condition} data-test-id="colour-style-condition">
          <div
            className={styles.operation}
            data-operation={filterOperation.toLowerCase()}
          >
            {filterOperation}
          </div>
          <div className={styles.conditionValue}>
            {i18next.t(`keys.${filterArray[0]}` as any)}
          </div>
          <div className={styles.conditionOperation}>
            {i18next.t(`operations.${filterArray[1]}` as any)}
          </div>
          <div className={styles.conditionValue}>
            {convertValue(filterArray[0], filterArray[2])}
          </div>
        </div>
      );
    }

    if (filterArray.length % 2 !== 1) {
      throw new Error(
        `Only even number of elements in filter is currently supported. Found ${
          filterArray.length
        } elements. Filter: ${JSON.stringify(filterArray)}`,
      );
    }

    const currentOperation = filterArray[1];
    return (
      <>
        {filterArray
          .filter((_filter, index) => index % 2 === 0)
          .map((filter) => {
            if (filter[1] === 'and' || filter[1] === 'or') {
              return (
                <details
                  className={styles.details}
                  onClick={(e) => e.stopPropagation()}
                >
                  <summary className={styles.condition}>
                    <div
                      className={styles.operation}
                      data-operation={currentOperation.toLowerCase()}
                    >
                      {currentOperation}
                    </div>
                    <div className={styles.conditionOperation}>{'Group'}</div>
                    <div>
                      <ArrowIcon className={styles.arrowIcon} />
                    </div>
                  </summary>

                  <div className={styles.groupContainer}>
                    {convert(
                      JSON.stringify(filter),
                      currentOperation.toString(),
                    )}
                  </div>
                </details>
              );
            } else {
              return (
                <>
                  {convert(JSON.stringify(filter), currentOperation.toString())}
                </>
              );
            }
          })}
      </>
    );
  }

  return <div className={styles.container}>{convert(condition)}</div>;
};

/*
  By default Dates in parsedFilterExpression are stored as strings.
  To correctly use them in Filters, we need to convert those strings to Dates.
  So, we pass an example object (to know which fields contain Date type) and if dummyObject contains a Date in certain field,
  we convert the string in Filter to Date as well
*/
export function convertDateTimeValuesInFilters(
  filterArray: any[],
  dataSourceElement: any,
) {
  if (!(filterArray instanceof Array)) {
    //throw new Error(`Invalid part of filter: ${JSON.stringify(filterArray)}`);
    return;
  }
  if (filterArray.length < 3) {
    //throw new Error(`Invalid part of filter: ${JSON.stringify(filterArray)}`);
    return;
  }

  if (typeof filterArray[0] === 'string') {
    //[["start",">=","2021-11-01T01:13:00Z"],"and",["comment","contains","123"],"and",["start","between",["2021-11-01T16:51:00Z","2021-11-20T16:51:00Z"]]]
    const value = filterArray[2];

    if (typeof value === 'string') {
      if (dataSourceElement[filterArray[0]] instanceof Date) {
        filterArray[2] = new Date(value);
      }
    }
    return;
  }

  for (let i = 0; i < filterArray.length; i += 2) {
    convertDateTimeValuesInFilters(filterArray[i], dataSourceElement);
  }
}
